import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from "react";
import styles from "./Toast.module.scss";
// eslint-disable-next-line import-x/default
import ReactDOM from "react-dom";

export type ToastType = "success" | "error";

export type ToastOptions = {
  title: string;
  description?: string;
  type?: ToastType;
  allowCopy?: boolean;
  partialDescription?: boolean;
  duration?: number; // ms before auto-dismiss, default 5000
};

type InternalToast = ToastOptions & {
  id: string;
};

type ToastContextValue = {
  toast: (opts: ToastOptions) => void;
};

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function ToastProvider({children}: { children: ReactNode }) {
  const [toasts, setToasts] = useState<InternalToast[]>([]);

  const remove = useCallback((id: string) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const toast = useCallback(
    (opts: ToastOptions) => {
      const id = Math.random().toString(36).slice(2);
      setToasts((t) => [...t, {id, ...opts}]);
      if (opts.duration !== 0) {
        const dur = opts.duration ?? 5000;
        setTimeout(() => remove(id), dur);
      }
    },
    [remove],
  );

  return (
    <ToastContext.Provider value={{toast}}>
      {children}
      {ReactDOM.createPortal(
        <div className={styles.container}>
          {toasts.map((t) => (
            <ToastItem key={t.id} toast={t} onClose={() => remove(t.id)}/>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be inside ToastProvider");
  return ctx.toast;
}

function ToastItem({
                     toast,
                     onClose,
                   }: {
  toast: InternalToast;
  onClose: () => void;
}) {
  const {
    title,
    description,
    type = "success",
    allowCopy = true,
    partialDescription = false,
  } = toast;
  const [copied, setCopied] = useState(false);

  const bgColor =
    type === "success" ? "#38a169" /* green.500 */ : "#e53e3e"; /* red.500 */

  const handleCopy = () => {
    if (description) {
      navigator.clipboard.writeText(description);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className={styles.toast} style={{background: bgColor}}>
      <div className={styles.header}>
        <strong className={styles.title}>{title}</strong>
        <button className={styles.closeBtn} onClick={onClose}>
          ×
        </button>
      </div>
      {description && (
        <div className={styles.body}>
          <p
            className={styles.desc}
            style={{
              WebkitLineClamp: partialDescription ? 2 : 10,
            }}
          >
            {description}
          </p>
          {allowCopy && type !== "error" && typeof description === "string" && (
            <button className={styles.copyBtn} onClick={handleCopy}>
              {copied ? "Copied" : "Copy"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
